<template>
  <div>
    <div
      class="cardLayoutWrapper"
      v-if="item"
    >
      <BasicCellActionIcon
        :span="1"
        icon-name="document-text"
        :deactivated="!item.estSousMandatGestion"
        :hover-messages="{ actif: 'Avec mandat de gestion', inactif: 'Sans mandat de gestion' }"
        :is-readonly="true"
      />
      <BasicCellActionIcon
        :span="1"
        icon-name="wallet"
        :deactivated="!item.avecSubrogation"
        :hover-messages="{ actif: 'Avec subrogation', inactif: 'Sans subrogation' }"
        :is-readonly="true"
      />
      <BasicCellActionIcon
        :span="1"
        icon-name="profile-circle"
        :deactivated="!estProprietaire"
        :hover-messages="{ actif: 'Ma demande', inactif: '' }"
        :is-readonly="true"
      />
      <BasicCell
        :span="2"
        extra-class="elem--clickable"
      >
        <span @click="ouvrirDemande(item)">
          {{ item?.id }}
        </span>
      </BasicCell>
      <BasicCell :span="2">{{ item.dispositif }}</BasicCell>
      <BasicCell
        :span="3"
        extra-class="elem--nowrap"
      >
        {{ SousDispositifLibelle[item.sousDispositif as SousDispositif] }}
      </BasicCell>
      <BasicCell
        :span="4"
        extra-class="elem--nowrap"
      >
        <span>
          {{ item?.intitule }}
        </span>
      </BasicCell>
      <BasicCell :span="3">{{ dateFormatter(item.dateFormation?.start) }}</BasicCell>
      <BasicCell :span="3">{{ dateFormatter(item.dateFormation?.end) }}</BasicCell>
      <BasicCell
        :span="5"
        extra-class="elem--nowrap"
      >
        <span>
          {{ item?.adherent?.denomination }}
        </span>
      </BasicCell>
      <BasicCell
        :span="3"
        extra-class="elem--clickable elem--nowrap"
      >
        <span @click="showDrawer = !showDrawer">
          {{ stagiaires }}
        </span>
      </BasicCell>
      <BasicCell :span="4">
        <BasicTooltip :label="StatutLibelle[item.statut]">
          <BasicBadge
            :type="explicitCast(StatutBadgeType[item.statut])"
            :deletable="false"
            :label="StatutLibelle[item.statut]"
          />
        </BasicTooltip>
      </BasicCell>
      <BasicCell :span="1" />
      <BasicCellActionIcon
        :span="1"
        :icon-name="readonly ? 'eye' : 'edit'"
        :hover-messages="{ actif: readonly ? 'Voir la demande' : 'Modifier la demande' }"
        @click="ouvrirDemande(item)"
      />
      <BasicCellActionIcon
        :span="1"
        icon-name="duplicate"
        :icon-props="{ disabled: !canClone }"
        :hover-messages="{ actif: 'Dupliquer la demande', inactif: 'Duplication non autorisée' }"
        :deactivated="!canClone"
        @click="clickClone"
      />
      <BasicCellActionIcon
        :span="1"
        icon-name="trash"
        :icon-props="{ disabled: !deletable || readonly }"
        :is-readonly="readonly"
        :deactivated="!deletable"
        :hover-messages="{ actif: 'Supprimer la demande', inactif: 'Suppression non autorisée' }"
        @click="() => deleteDemande(item.id, item.dispositif, item.sousDispositif)"
      />
    </div>
    <Transition name="open">
      <div
        v-if="showDrawer && (stagiairesList.length || prestatairesList.length)"
        class="listDrawerWrapper"
      >
        <div class="content">
          <ListDetails>
            <template #content>
              <div
                class="listDetailsElement"
                v-if="stagiairesList.length"
              >
                <span class="listDetailsTitle">Stagiaire(s)</span>
                <span class="listDetailsSubtitle">
                  {{ stagiairesList }}
                </span>
              </div>
              <div
                class="listDetailsElement"
                v-if="prestatairesList.length"
              >
                <span class="listDetailsTitle">Organisme(s)</span>
                <span
                  class="listDetailsSubtitle"
                  v-for="prestataire in prestatairesList"
                  :key="prestataire"
                >
                  {{ prestataire }}
                </span>
              </div>
            </template>
          </ListDetails>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import type { RechercheResultDTO } from '@/features/api/extranet/dto/Demande/Recherche'
  import { StatutBadgeType, StatutLibelle } from '@/features/domaine/types/opco'
  import { explicitCast } from '@/features/interface/shared/tools'
  import { dateFormatter } from '@/features/shared/tools/date'
  import { computed, ref } from 'vue'
  import { useRechercheDemandeActions } from '../useRechercheDemandeActions'
  import { useDemandeHelper } from './../useDemandeHelper'
  import ListDetails from './ListDetails.vue'
  import { SousDispositifLibelle } from '@/features/domaine/types/opco/Demande/DR'
  import type { SousDispositif } from '@/features/domaine/types/opco/Demande/DR'
  import BasicCell from '@designSystem/components/basic/cell/BasicCell.vue'
  import BasicCellActionIcon from '@designSystem/components/basic/cell/BasicCellActionIcon.vue'

  const { cloneDemande, deleteDemande, ouvrirDemande } = useRechercheDemandeActions('Formation')

  const props = defineProps<{ item: RechercheResultDTO }>()
  const { readonly, stagiaires, stagiairesList, prestatairesList, checkClonePermission, deletable } = useDemandeHelper(
    props.item,
  )
  const showDrawer = ref(false)

  const estProprietaire = computed(() => !!props.item?.estProprietaire)

  const canClone = computed(() => checkClonePermission(props.item.dispositif))

  const clickClone = () => {
    if (!canClone.value) return
    return cloneDemande({
      dispositif: props.item.dispositif,
      idDemande: props.item.id,
      idcc: props.item.adherent.idcc!.code,
      siret: props.item.adherent.siret.valeur!,
      permissions: props.item.niveauPermission,
      sousDispositif: props.item.sousDispositif,
      codeDrf: props.item.codeParent!,
    })
  }
</script>

<style scoped>
  @import '../../index.less';
</style>

